import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFilter, faTimes, faCalendarAlt,
  faUser, faIdCard, faCheckCircle, faExclamationTriangle,
  faClock, faEye, faSyncAlt, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
  }
};

const API_URL = `${API_CONFIG2.apiUrl}presence/api_presence_grouped.php`;

const PresenceGrouped = () => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modal détails
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [details, setDetails] = useState([]);
  const [stats, setStats] = useState({ total: 0, present: 0, retard: 0, absent: 0 });
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const searchTimeoutRef = useRef(null);

  const statutOptions = [
    { value: 'P', label: 'Présent' },
    { value: 'R', label: 'Retard' },
    { value: 'A', label: 'Absent' }
  ];

  // Récupération des jours
  const fetchDays = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        action: 'fetch',
        search,
        statut: statutFilter,
        date_from: dateFrom,
        date_to: dateTo,
        unique: new Date().getTime()
      });
      const url = `${API_URL}?${params.toString()}`;
      const data = await fetchJSON(url, { signal: controller.signal });
      if (data.status === 'success') {
        setDays(data.data);
      } else {
        throw new Error(data.message || 'Erreur de chargement');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [search, statutFilter, dateFrom, dateTo]);

  // Rafraîchissement
  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchDays();
    setRefreshing(false);
    setToast({ show: true, message: 'Données actualisées', variant: 'success' });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
  }, [fetchDays]);

  // Effet pour les filtres (avec debounce)
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchDays();
    }, 400);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [search, statutFilter, dateFrom, dateTo, fetchDays]);

  // Chargement initial
  useEffect(() => {
    isMountedRef.current = true;
    fetchDays();
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Ouvrir le modal avec les détails d'un jour
  const openDetails = async (date) => {
    setSelectedDate(date);
    setShowModal(true);
    setLoadingDetails(true);
    try {
      const params = new URLSearchParams({ action: 'details', date });
      const url = `${API_URL}?${params.toString()}`;
      const data = await fetchJSON(url);
      if (data.status === 'success') {
        setDetails(data.data.details);
        setStats(data.data.stats);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, variant: 'danger' });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
      setShowModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Effacer les filtres
  const clearFilters = () => {
    setSearch('');
    setStatutFilter('');
    setDateFrom('');
    setDateTo('');
  };

  // Obtenir le chemin de la photo
  const getPhotoUrl = (photo) => {
    if (!photo || photo === 'default.png') {
      return '/assets/default-avatar.png';
    }
    return `${API_CONFIG2.apiUrl}uploads/employes/${photo}`;
  };

  // Statut badge CSS
  const getStatutClass = (statut) => {
    switch (statut) {
      case 'P': return 'statut-present';
      case 'R': return 'statut-retard';
      case 'A': return 'statut-absent';
      default: return '';
    }
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'P': return 'Présent';
      case 'R': return 'Retard';
      case 'A': return 'Absent';
      default: return statut;
    }
  };

  // Couleur pour les statistiques
  const getStatColor = (statut) => {
    switch (statut) {
      case 'P': return '#22c55e';
      case 'R': return '#f59e0b';
      case 'A': return '#ef4444';
      default: return '#5e6f94';
    }
  };

  return (
    <>
      <style>{`
        :root {
          --accent-blue: #4f8cff;
          --accent-green: #22c55e;
          --accent-orange: #f59e0b;
          --accent-red: #ef4444;
          --bg-start: #0d1b2a;
          --bg-end: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.5);
          --card-border: rgba(79, 140, 255, 0.15);
          --text-primary: #e8edff;
          --text-secondary: #a5b4d9;
          --text-muted: #5e6f94;
          --radius: 20px;
          --shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
        }
        * { box-sizing: border-box; }
        .monetra-page {
          background: radial-gradient(ellipse at 20% 30%, #132a4a, #0a0f1f);
          min-height: 100vh;
          padding: clamp(0.8rem, 2vw, 2.5rem);
          color: var(--text-primary);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: clamp(1rem, 2vw, 2rem);
          box-shadow: var(--shadow);
          transition: all 0.25s ease;
        }
        .glass-card:hover {
          border-color: rgba(79, 140, 255, 0.25);
          box-shadow: 0 16px 48px rgba(79, 140, 255, 0.05);
        }
        .monetra-btn {
          padding: 8px 18px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.95rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.3);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px rgba(79, 140, 255, 0.25);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: #2563eb;
          box-shadow: 0 8px 28px rgba(79, 140, 255, 0.4);
        }
        .monetra-btn.outline {
          border-color: rgba(255,255,255,0.1);
        }
        .search-wrapper {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 2px 2px 2px 20px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(79, 140, 255, 0.1);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }
        .filter-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          color: var(--text-primary);
          padding: 8px 14px;
          outline: none;
          min-width: 120px;
          transition: 0.3s;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          min-height: 44px;
        }
        .filter-select:focus {
          border-color: var(--accent-blue);
        }
        .filter-select option {
          background: #0d1b2a;
          color: var(--text-primary);
        }

        .day-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1.2rem;
          margin-top: 1rem;
        }
        .day-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 1.2rem;
          cursor: pointer;
          transition: 0.2s;
        }
        .day-card:hover {
          background: rgba(255,255,255,0.06);
          transform: translateY(-4px);
          border-color: rgba(79, 140, 255, 0.3);
          box-shadow: 0 8px 24px rgba(0,0,0,0.3);
        }
        .day-card .date {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .day-card .stats {
          display: flex;
          justify-content: space-around;
          margin-top: 0.5rem;
        }
        .day-card .stat-item {
          text-align: center;
        }
        .day-card .stat-item .count {
          font-size: 1.5rem;
          font-weight: 700;
        }
        .day-card .stat-item .label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
        }
        .day-card .view-btn {
          margin-top: 0.8rem;
          width: 100%;
        }

        .modal-details .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(79, 140, 255, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .modal-details .modal-header {
          border-bottom: 1px solid rgba(79, 140, 255, 0.06);
        }
        .modal-details .modal-footer {
          border-top: 1px solid rgba(79, 140, 255, 0.06);
        }
        .modal-details .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.6rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.03);
        }
        .detail-item:last-child {
          border-bottom: none;
        }
        .detail-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(255,255,255,0.08);
          background: #1e293b;
        }
        .detail-info {
          flex: 1;
        }
        .detail-name {
          font-weight: 500;
        }
        .detail-matricule {
          font-size: 0.8rem;
          color: var(--text-muted);
        }
        .detail-statut {
          font-weight: 600;
          padding: 2px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
        }
        .statut-present { background: rgba(34, 197, 94, 0.15); color: #22c55e; }
        .statut-retard { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
        .statut-absent { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

        .stats-summary {
          display: flex;
          gap: 1.5rem;
          justify-content: center;
          margin: 0.5rem 0 1rem;
        }
        .stats-summary .stat-box {
          text-align: center;
          padding: 0.5rem 1rem;
          background: rgba(255,255,255,0.03);
          border-radius: 12px;
          min-width: 80px;
        }
        .stats-summary .stat-box .num {
          font-size: 1.4rem;
          font-weight: 700;
        }
        .stats-summary .stat-box .lbl {
          font-size: 0.7rem;
          color: var(--text-muted);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.85) !important;
          backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(79, 140, 255, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-green); }
        .text-danger { color: var(--accent-red); }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-center { text-align: center; }
        .ms-auto { margin-left: auto; }

        @media (max-width: 768px) {
          .monetra-page { padding: 10px; }
          .glass-card { padding: 1rem; border-radius: 16px; }
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }
          .day-grid {
            grid-template-columns: 1fr;
          }
          .filter-select {
            min-width: unset;
            width: 100%;
          }
          .search-wrapper {
            min-width: unset;
            width: 100%;
          }
          .stats-summary {
            flex-wrap: wrap;
            gap: 0.8rem;
          }
        }
      `}</style>

      <div className="monetra-page">
        {/* En-tête */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Présence groupée par jour
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              {days.length} jour(s) enregistré(s)
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="monetra-btn" onClick={refresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
          </div>
        </div>

        {/* Barre de filtres */}
        <div className="glass-card mb-4 d-flex flex-wrap gap-3 align-items-center">
          <div className="search-wrapper" style={{ minWidth: '200px', flex: 1 }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (nom, matricule...)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="btn"
                onClick={() => setSearch('')}
                style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: '0 10px' }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <FontAwesomeIcon icon={faFilter} className="text-secondary" />
            <select
              className="filter-select"
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value)}
            >
              <option value="">Tous statuts</option>
              {statutOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <input
              type="date"
              className="filter-select"
              style={{ minWidth: '130px' }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder="Du"
            />
            <input
              type="date"
              className="filter-select"
              style={{ minWidth: '130px' }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder="Au"
            />
            {(search || statutFilter || dateFrom || dateTo) && (
              <button className="monetra-btn outline" onClick={clearFilters}>
                <FontAwesomeIcon icon={faTimes} /> Effacer
              </button>
            )}
          </div>
        </div>

        {/* Grille des jours */}
        <div className="glass-card">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-secondary mt-3">Chargement...</p>
            </div>
          ) : days.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune donnée de présence trouvée.</div>
          ) : (
            <div className="day-grid">
              {days.map(day => (
                <div key={day.date} className="day-card" onClick={() => openDetails(day.date)}>
                  <div className="date">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    {new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                  <div className="stats">
                    <div className="stat-item">
                      <div className="count" style={{ color: getStatColor('P') }}>{day.present}</div>
                      <div className="label">Présents</div>
                    </div>
                    <div className="stat-item">
                      <div className="count" style={{ color: getStatColor('R') }}>{day.retard}</div>
                      <div className="label">Retards</div>
                    </div>
                    <div className="stat-item">
                      <div className="count" style={{ color: getStatColor('A') }}>{day.absent}</div>
                      <div className="label">Absents</div>
                    </div>
                  </div>
                  <div className="stat-item" style={{ marginTop: '0.3rem' }}>
                    <span className="text-secondary small">Total: {day.total}</span>
                  </div>
                  <button className="monetra-btn primary view-btn" onClick={(e) => { e.stopPropagation(); openDetails(day.date); }}>
                    <FontAwesomeIcon icon={faEye} /> Voir détails
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* MODAL DÉTAILS */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="modal-details">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
            Détails du {selectedDate ? new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-secondary mt-3">Chargement des détails...</p>
            </div>
          ) : (
            <>
              <div className="stats-summary">
                <div className="stat-box">
                  <div className="num" style={{ color: '#5e6f94' }}>{stats.total}</div>
                  <div className="lbl">Total</div>
                </div>
                <div className="stat-box">
                  <div className="num" style={{ color: '#22c55e' }}>{stats.present}</div>
                  <div className="lbl">Présents</div>
                </div>
                <div className="stat-box">
                  <div className="num" style={{ color: '#f59e0b' }}>{stats.retard}</div>
                  <div className="lbl">Retards</div>
                </div>
                <div className="stat-box">
                  <div className="num" style={{ color: '#ef4444' }}>{stats.absent}</div>
                  <div className="lbl">Absents</div>
                </div>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {details.map(emp => (
                  <div key={emp.employeId} className="detail-item">
                    <img
                      src={`${API_CONFIG2.apiUrl}${emp.photo}`}
                      alt={`${emp.prenom} ${emp.nom}`}
                      className="detail-avatar"
                      onError={(e) => { e.target.src = '/assets/default-avatar.png'; }}
                    />
                    <div className="detail-info">
                      <div className="detail-name">{emp.prenom} {emp.nom}</div>
                      <div className="detail-matricule"><FontAwesomeIcon icon={faIdCard} className="me-1" />{emp.matricule}</div>
                    </div>
                    <span className={`detail-statut ${getStatutClass(emp.statut)}`}>
                      {getStatutLabel(emp.statut)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="monetra-btn" onClick={() => setShowModal(false)}>Fermer</button>
        </Modal.Footer>
      </Modal>

      {/* TOAST */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button
                className="ms-auto"
                onClick={() => setToast(prev => ({ ...prev, show: false }))}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PresenceGrouped;